'use client';

import { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSession } from 'next-auth/react';
import { InputMask } from '@react-input/mask';
import submitTradeProgramAction from '@/app/_lib/actions/SubmitTradeProgramAction';
import { useGlobalMessage } from '@/app/_lib/hooks';
import { useReCaptcha } from 'next-recaptcha-v3';
import getBillingStatesAction from '@/app/_lib/actions/GetBillingStatesAction';
import getCustomerBillingAddressAction from '@/app/_lib/actions/GetCustomerBillingAddressAction';
import MyModal from '@/app/_components/MyModal';
import TermsModalContent from '@/app/_components/TermsModalContent';
import RecaptchaDisclaimer from '@/app/_components/RecaptchaDisclaimer';
import GooglePlacesAutocomplete from '@/app/_components/GooglePlacesAutocomplete';

// Strip leading country code (1 or +1) from phone numbers
const phoneTrack = ({
  inputType,
  data
}) => {
  if (inputType === 'insert' && data) {
    let digits = data.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return digits.slice(1);
    }
    return digits;
  }
  return data;
};
const TradeProgramForm = () => {
  const {
    data: session
  } = useSession();
  const addGlobalMessage = useGlobalMessage();
  const {
    executeRecaptcha
  } = useReCaptcha();
  const [billingStates, setBillingStates] = useState([]);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: {
      errors,
      isSubmitting
    }
  } = useForm({
    defaultValues: {
      email: '',
      name: '',
      company_name: '',
      web_site: '',
      trade_company_type: '',
      street_address: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      comments: '',
      terms_accepted: false
    }
  });
  const termsAccepted = watch('terms_accepted');
  const stateValue = watch('state');
  const tradeTypeValue = watch('trade_company_type');
  useEffect(() => {
    const setUp = async () => {
      const billingStatesResponse = await getBillingStatesAction();
      if (billingStatesResponse.success && billingStatesResponse.states) {
        setBillingStates(billingStatesResponse.states);
      }
      if (session?.user?.customerCode) {
        const billingResponse = await getCustomerBillingAddressAction();
        if (billingResponse.success && billingResponse.address) {
          const addr = billingResponse.address;
          reset({
            email: addr.email || session.user.email || '',
            name: addr.name || session.user.name || '',
            company_name: '',
            web_site: '',
            street_address: addr.street_address || '',
            city: addr.city || '',
            state: addr.state || '',
            zip: addr.zip || '',
            phone: addr.phone || '',
            comments: '',
            terms_accepted: false
          });
        }
      }
    };
    setUp();
  }, [session, reset]);
  const openTermsModal = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setTermsModalOpen(true);
  }, []);
  const handleAddressSelected = addressData => {
    if (addressData.street_1) setValue('street_address', addressData.street_1);
    if (addressData.city) setValue('city', addressData.city);
    if (addressData.state) setValue('state', addressData.state);
    if (addressData.zip) setValue('zip', addressData.zip);
  };
  const onSubmit = async data => {
    try {
      const token = await executeRecaptcha('trade_program_signup');
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (key !== 'terms_accepted') {
          formData.set(key, value);
        }
      });
      formData.set('recaptcha_token', token);
      const response = await submitTradeProgramAction(formData);
      if (response.success) {
        addGlobalMessage({
          message: 'Thank you for your interest! We will be in touch within one business day.',
          messageType: 'success'
        });
        reset();
      } else {
        addGlobalMessage({
          message: response.message || 'An error occurred. Please try again.',
          messageType: 'error'
        });
      }
    } catch (error) {
      addGlobalMessage({
        message: 'An error occurred. Please try again.',
        messageType: 'error'
      });
    }
  };
  return <>
            <form onSubmit={handleSubmit(onSubmit)}>
                {/* Row 1: Name and Email */}
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                    <div className="w-full">
                        <div className="form-group">
                            <input id="name" type="text" autoComplete="name" className="form-input peer" placeholder=" " disabled={isSubmitting} {...register('name', {
              required: 'Name is required'
            })} />
                            <label htmlFor="name" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                Name <span className="text-primary">*</span>
                            </label>
                        </div>
                        {errors.name && <p className="text-red text-xs mt-1">{errors.name.message}</p>}
                    </div>
                    <div className="w-full">
                        <div className="form-group">
                            <input id="email" type="email" autoComplete="email" className="form-input peer" placeholder=" " disabled={isSubmitting} {...register('email', {
              required: 'Email is required'
            })} />
                            <label htmlFor="email" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                Email Address <span className="text-primary">*</span>
                            </label>
                        </div>
                        {errors.email && <p className="text-red text-xs mt-1">{errors.email.message}</p>}
                    </div>
                </div>

                {/* Row 2: Company Name and Website */}
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                    <div className="w-full">
                        <div className="form-group">
                            <input id="company_name" type="text" autoComplete="organization" className="form-input peer" placeholder=" " disabled={isSubmitting} {...register('company_name', {
              required: 'Firm name is required'
            })} />
                            <label htmlFor="company_name" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                Firm Name <span className="text-primary">*</span>
                            </label>
                        </div>
                        {errors.company_name && <p className="text-red text-xs mt-1">{errors.company_name.message}</p>}
                    </div>
                    <div className="w-full">
                        <div className="form-group">
                            <input id="web_site" type="text" className="form-input peer" placeholder=" " disabled={isSubmitting} {...register('web_site', {
              required: 'Company website is required'
            })} />
                            <label htmlFor="web_site" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                Company Website <span className="text-primary">*</span>
                            </label>
                        </div>
                        {errors.web_site && <p className="text-red text-xs mt-1">{errors.web_site.message}</p>}
                    </div>
                </div>

                {/* Trade Type */}
                <div className="mb-4">
                    <div className="form-group">
                        <select id="trade_company_type" className="form-input peer" disabled={isSubmitting} {...register('trade_company_type', {
            required: 'Your trade is required'
          })}>
                            <option value=""></option>
                            <option value="architect">architect</option>
                            <option value="interior designer">interior designer</option>
                            <option value="other">other (specify below)</option>
                        </select>
                        <label htmlFor="trade_company_type" className={`form-input-label ${!tradeTypeValue ? 'form-input-label-placeholder-shown' : ''}`}>
                            Your Trade <span className="text-primary">*</span>
                        </label>
                    </div>
                    {errors.trade_company_type && <p className="text-red text-xs mt-1">{errors.trade_company_type.message}</p>}
                </div>

                {/* Street Address with Google Places */}
                <div className="mb-4">
                    <Controller name="street_address" control={control} rules={{
          required: 'Address is required'
        }} render={({
          field
        }) => <GooglePlacesAutocomplete id="street_address" name="street_address" label={<>Address <span className="text-primary">*</span></>} onPlaceSelected={handleAddressSelected} value={field.value} onChange={e_0 => field.onChange(e_0.target.value)} disabled={isSubmitting} />} />
                    {errors.street_address && <p className="text-red text-xs mt-1">{errors.street_address.message}</p>}
                </div>

                {/* City, State, ZIP */}
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                    <div className="w-full md:w-1/2">
                        <div className="form-group">
                            <input id="city" type="text" autoComplete="address-level2" className="form-input peer" placeholder=" " disabled={isSubmitting} {...register('city', {
              required: 'City is required'
            })} />
                            <label htmlFor="city" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                City <span className="text-primary">*</span>
                            </label>
                        </div>
                        {errors.city && <p className="text-red text-xs mt-1">{errors.city.message}</p>}
                    </div>
                    <div className="w-full md:w-1/4">
                        <div className="form-group">
                            <select id="state" autoComplete="address-level1" className="form-input peer" disabled={isSubmitting} {...register('state', {
              required: 'State is required'
            })}>
                                <option value=""></option>
                                {billingStates.map(state => <option key={state.state_id} value={state.abbr}>
                                        {state.state_name}
                                    </option>)}
                            </select>
                            <label htmlFor="state" className={`form-input-label ${!stateValue ? 'form-input-label-placeholder-shown' : ''}`}>
                                State <span className="text-primary">*</span>
                            </label>
                        </div>
                        {errors.state && <p className="text-red text-xs mt-1">{errors.state.message}</p>}
                    </div>
                    <div className="w-full md:w-1/4">
                        <div className="form-group">
                            <input id="zip" type="text" autoComplete="postal-code" inputMode="numeric" pattern="[0-9]*" maxLength="5" className="form-input peer" placeholder=" " disabled={isSubmitting} {...register('zip', {
              required: 'ZIP code is required'
            })} />
                            <label htmlFor="zip" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                ZIP <span className="text-primary">*</span>
                            </label>
                        </div>
                        {errors.zip && <p className="text-red text-xs mt-1">{errors.zip.message}</p>}
                    </div>
                </div>

                {/* Phone */}
                <div className="mb-4">
                    <div className="form-group">
                        <Controller name="phone" control={control} rules={{
            required: 'Phone is required'
          }} render={({
            field: field_0
          }) => <InputMask mask="(___) ___-____" replacement={{
            _: /\d/
          }} track={phoneTrack} id="phone" type="tel" autoComplete="tel" inputMode="tel" className="form-input peer" placeholder=" " disabled={isSubmitting} value={field_0.value} onChange={field_0.onChange} onBlur={field_0.onBlur} />} />
                        <label htmlFor="phone" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                            Phone <span className="text-primary">*</span>
                        </label>
                    </div>
                    {errors.phone && <p className="text-red text-xs mt-1">{errors.phone.message}</p>}
                </div>

                {/* Comments */}
                <div className="form-group mb-4">
                    <textarea id="comments" className="form-input peer" placeholder=" " rows="4" disabled={isSubmitting} {...register('comments')} />
                    <label htmlFor="comments" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                        Comments
                    </label>
                </div>

                {/* Terms Checkbox */}
                <div className="flex items-center gap-3 mb-4">
                    <input id="terms-checkbox" type="checkbox" disabled={isSubmitting} className="checkbox-switch" {...register('terms_accepted', {
          validate: value_0 => value_0 === true || 'You must accept the terms and conditions'
        })} />
                    <label htmlFor="terms-checkbox" className="!text-xs">
                        <span className="text-light">I agree to the hive </span>
                        <button onClick={openTermsModal} type="button" className="underline hover:text-primary">
                            Terms and Conditions
                        </button>
                    </label>
                </div>
                {errors.terms_accepted && <p className="text-red text-xs mb-4">{errors.terms_accepted.message}</p>}

                {/* Submit Button */}
                <div className="mb-6">
                    <button type="submit" disabled={isSubmitting} className={`btn btn-primary w-auto px-10 ${isSubmitting ? 'opacity-50' : ''}`}>
                        {isSubmitting ? 'Submitting...' : 'Submit Application'}
                    </button>
                </div>

                <RecaptchaDisclaimer />
            </form>

            <MyModal isOpen={termsModalOpen} setIsOpen={setTermsModalOpen} contentLabel="hive Terms and Conditions">
                <TermsModalContent />
            </MyModal>
        </>;
};
export default TradeProgramForm;